import React, { useState, useEffect, useRef, useCallback } from 'react';
import './Tutorial.css';
import Tile from '../Tile/Tile';
import Player from '../Player/Player';
import CollectEffect from './CollectEffect';
import { ArrowUp, ArrowDown, ArrowLeft, ArrowRight, Move, Coins, Trophy, Timer,
    ChevronRight, CheckCircle } from 'lucide-react';
import { playRandomPopSound, playBumpSound, playCollectSound, playCoinSound,
    playBlingSound, playGridLevelUpSound } from '../utils/sounds';

/*
 * Each step requires reaching the green goal tile to advance.
 * Player position carries over between steps.
 * A completion banner shows before transitioning.
 */

const STEPS = [
    {
        id: 'move',
        title: 'MOVE YOUR PLAYER',
        subtitle: 'Swipe or use arrow keys to move',
        size: 3,
        grid: [
            [0, 0, 0],
            [0, 0, 0],
            [0, 0, 2],
        ],
        playerStart: { x: 0, y: 0 },
        items: {},
        banner: 'NICE!',
    },
    {
        id: 'coins',
        title: 'COLLECT COINS',
        subtitle: 'Grab coins on your way to the goal',
        size: 3,
        grid: [
            [0, 0, 0],
            [0, 0, 0],
            [0, 0, 2],
        ],
        playerStart: null, // inherit from previous
        items: { '0-2': 5, '1-1': 5, '2-0': 5 },
        banner: 'GREAT!',
    },
    {
        id: 'walls',
        title: 'AVOID THE WALLS',
        subtitle: "Find a path around — you can't move through walls",
        size: 3,
        // Player starts at bottom-right (2,2). Goal is top-left (0,0).
        // Walls force a path around, not straight through.
        grid: [
            [2, 0, 0],
            [1, 1, 0],
            [0, 0, 0],
        ],
        playerStart: null,
        items: {},
        banner: 'SMART!',
    },
    {
        id: 'growth',
        title: 'THE GRID GROWS',
        subtitle: 'Each goal makes the grid bigger and harder',
        size: 4,
        // Player comes from top-left (0,0). Goal at bottom-right (3,3).
        // A wall cluster in the middle to force navigation.
        grid: [
            [0, 0, 0, 0],
            [0, 1, 1, 0],
            [0, 1, 0, 0],
            [0, 0, 0, 2],
        ],
        playerStart: null,
        items: { '0-3': 5, '3-0': 5 },
        banner: 'BIGGER!',
    },
    {
        id: 'summary',
        title: 'YOU\'RE ALL SET',
        subtitle: 'Beat the timer, score points, collect coins — good luck!',
        size: 0,
        grid: null,
        playerStart: null,
        items: null,
        banner: null,
    },
];

function Tutorial({ onComplete }) {
    const [stepIndex, setStepIndex] = useState(0);
    const [isExiting, setIsExiting] = useState(false);
    const [textFading, setTextFading] = useState(false);

    // Grid state
    const [grid, setGrid] = useState(STEPS[0].grid);
    const [gridSize, setGridSize] = useState(STEPS[0].size);
    const [playerPos, setPlayerPos] = useState(STEPS[0].playerStart);
    const [items, setItems] = useState({ ...STEPS[0].items });
    const [triggerPop, setTriggerPop] = useState(1);
    const [collectEffects, setCollectEffects] = useState([]);

    // Completion banner
    const [banner, setBanner] = useState(null); // { text: string }
    const [bannerExiting, setBannerExiting] = useState(false);

    // Refs
    const gridRef = useRef(null);
    const [tileSize, setTileSize] = useState(0);
    const [gapSize, setGapSize] = useState(0);
    const touchStartRef = useRef(null);
    const advancingRef = useRef(false);
    const stepRef = useRef(0);
    const playerPosRef = useRef(STEPS[0].playerStart);

    const step = STEPS[stepIndex];
    const isLastStep = stepIndex === STEPS.length - 1;
    const hasGrid = step.grid !== null;
    const progress = ((stepIndex + 1) / STEPS.length) * 100;

    // Keep refs in sync
    useEffect(() => { stepRef.current = stepIndex; }, [stepIndex]);
    useEffect(() => { playerPosRef.current = playerPos; }, [playerPos]);

    // Measure tile size
    useEffect(() => {
        const measure = () => {
            if (gridRef.current && gridSize > 0) {
                const gap = parseFloat(getComputedStyle(gridRef.current).gap) || 0;
                setGapSize(gap);
                const totalGap = gap * (gridSize - 1);
                setTileSize((gridRef.current.offsetWidth - totalGap) / gridSize);
            }
        };
        measure();
        window.addEventListener('resize', measure);
        return () => window.removeEventListener('resize', measure);
    }, [gridSize, grid]);

    // --- ADVANCE TO NEXT STEP ---
    const advanceStep = useCallback(() => {
        if (advancingRef.current) return;
        advancingRef.current = true;

        const currentIdx = stepRef.current;
        const currentStep = STEPS[currentIdx];
        const nextIndex = currentIdx + 1;
        if (nextIndex >= STEPS.length) { advancingRef.current = false; return; }

        const nextStep = STEPS[nextIndex];

        // 1. Show completion banner
        if (currentStep.banner) {
            playBlingSound(false);
            setBanner({ text: currentStep.banner });
            setBannerExiting(false);
        }

        // 2. After banner shows, fade text and transition
        const bannerDuration = currentStep.banner ? 1200 : 200;

        setTimeout(() => {
            // Start banner exit
            if (currentStep.banner) setBannerExiting(true);

            // Fade out text
            setTextFading(true);

            setTimeout(() => {
                setBanner(null);
                setBannerExiting(false);

                // Update step
                setStepIndex(nextIndex);

                // Carry player position forward, clamped to new grid
                let newPlayerPos;
                if (nextStep.playerStart) {
                    newPlayerPos = nextStep.playerStart;
                } else {
                    const prev = playerPosRef.current;
                    const newSize = nextStep.size || 3;
                    newPlayerPos = {
                        x: Math.min(prev.x, newSize - 1),
                        y: Math.min(prev.y, newSize - 1),
                    };
                    // Make sure we don't land on a wall or goal in the new grid
                    if (nextStep.grid && nextStep.grid[newPlayerPos.y]?.[newPlayerPos.x] !== 0) {
                        // Find nearest empty cell
                        for (let r = 0; r < newSize; r++) {
                            for (let c = 0; c < newSize; c++) {
                                if (nextStep.grid[r][c] === 0) {
                                    newPlayerPos = { x: c, y: r };
                                    r = newSize; // break outer
                                    break;
                                }
                            }
                        }
                    }
                }

                if (nextStep.grid) {
                    const sizeChanged = nextStep.size !== STEPS[currentIdx].size;
                    if (sizeChanged) playGridLevelUpSound(false);

                    setGrid(nextStep.grid);
                    setGridSize(nextStep.size);
                    setPlayerPos(newPlayerPos);
                    setItems({ ...(nextStep.items || {}) });
                    setTriggerPop(prev => prev + 1);
                }

                setTextFading(false);
                advancingRef.current = false;
            }, 300);
        }, bannerDuration);
    }, []);

    // --- GOAL HIT HANDLER ---
    const onGoalReached = useCallback(() => {
        advanceStep();
    }, [advanceStep]);

    // --- MOVEMENT ---
    const attemptMove = useCallback((dx, dy) => {
        if (advancingRef.current || !grid) return;

        setPlayerPos(prev => {
            const newX = prev.x + dx;
            const newY = prev.y + dy;
            const size = grid.length;

            if (newX < 0 || newX >= size || newY < 0 || newY >= size) return prev;

            if (grid[newY][newX] === 1) {
                playBumpSound(false);
                return prev;
            }

            playRandomPopSound(false);

            // Coin pickup
            const itemKey = `${newY}-${newX}`;
            if (items[itemKey] === 5) {
                playCoinSound(false);
                if (tileSize > 0) {
                    const id = Date.now() + Math.random();
                    setCollectEffects(prev => [...prev, {
                        id,
                        x: newX * (tileSize + gapSize),
                        y: newY * (tileSize + gapSize),
                        size: tileSize,
                        color: '#ffd700',
                    }]);
                    setTimeout(() => setCollectEffects(prev => prev.filter(e => e.id !== id)), 800);
                }
                setItems(prev => {
                    const next = { ...prev };
                    delete next[itemKey];
                    return next;
                });
            }

            // Goal reached
            if (grid[newY][newX] === 2) {
                playCollectSound(false);
                if (tileSize > 0) {
                    const id = Date.now() + Math.random();
                    setCollectEffects(prev => [...prev, {
                        id,
                        x: newX * (tileSize + gapSize),
                        y: newY * (tileSize + gapSize),
                        size: tileSize,
                        color: '#00ffa3',
                    }]);
                    setTimeout(() => setCollectEffects(prev => prev.filter(e => e.id !== id)), 800);
                }
                // Defer goal handling so player position updates first
                setTimeout(() => onGoalReached(), 50);
            }

            return { x: newX, y: newY };
        });
    }, [grid, items, tileSize, gapSize, onGoalReached]);

    // --- INPUT HANDLERS ---
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.repeat) return;
            switch (e.key) {
                case 'ArrowUp': case 'w': case 'W': attemptMove(0, -1); break;
                case 'ArrowDown': case 's': case 'S': attemptMove(0, 1); break;
                case 'ArrowLeft': case 'a': case 'A': attemptMove(-1, 0); break;
                case 'ArrowRight': case 'd': case 'D': attemptMove(1, 0); break;
                default: break;
            }
        };
        const handleTouchStart = (e) => {
            touchStartRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
        };
        const handleTouchMove = (e) => {
            if (!touchStartRef.current) return;
            const deltaX = e.touches[0].clientX - touchStartRef.current.x;
            const deltaY = e.touches[0].clientY - touchStartRef.current.y;
            if (Math.abs(deltaX) > 25 || Math.abs(deltaY) > 25) {
                if (Math.abs(deltaX) > Math.abs(deltaY)) {
                    attemptMove(deltaX > 0 ? 1 : -1, 0);
                } else {
                    attemptMove(0, deltaY > 0 ? 1 : -1);
                }
                touchStartRef.current = null;
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        window.addEventListener('touchstart', handleTouchStart, { passive: false });
        window.addEventListener('touchmove', handleTouchMove, { passive: false });
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('touchstart', handleTouchStart);
            window.removeEventListener('touchmove', handleTouchMove);
        };
    }, [attemptMove]);

    // --- FINISH / SKIP ---
    const handleFinish = () => {
        setIsExiting(true);
        setTimeout(() => {
            localStorage.setItem('tileSprint_tutorialComplete', 'true');
            onComplete();
        }, 500);
    };

    const handleSkip = () => {
        setIsExiting(true);
        setTimeout(() => {
            localStorage.setItem('tileSprint_tutorialComplete', 'true');
            onComplete();
        }, 500);
    };

    const [isTouch, setIsTouch] = useState(false);
    useEffect(() => {
        setIsTouch('ontouchstart' in window || navigator.maxTouchPoints > 0);
    }, []);

    return (
        <div className={`tutorial-overlay ${isExiting ? 'tutorial-exit' : ''}`}>
            <div className="tutorial-container">
                {!isLastStep && (
                    <button className="tutorial-skip-btn" onClick={handleSkip}>SKIP</button>
                )}

                {/* Progress bar */}
                {!isLastStep && (
                    <div className="tutorial-progress">
                        <div className="tutorial-progress-fill" style={{ width: `${progress}%` }} />
                    </div>
                )}

                {!isLastStep && (
                    <div className="tutorial-step-counter">
                        {stepIndex + 1} / {STEPS.length}
                    </div>
                )}

                {/* Text area */}
                <div className={`tutorial-text-area ${textFading ? 'text-fade-out' : 'text-fade-in'}`}>
                    <h2 className="tutorial-title">{step.title}</h2>
                    <p className="tutorial-subtitle">{step.subtitle}</p>
                </div>

                {/* Grid area — banner is absolutely positioned inside so it doesn't shift layout */}
                <div className={`tutorial-game-area ${hasGrid ? '' : 'tutorial-game-hidden'}`}>
                    <div className="game-container tutorial-game-container">
                        <div className="grid-wrapper" style={{ width: '100%', height: '100%', aspectRatio: '1 / 1' }}>
                            <div
                                ref={gridRef}
                                className="grid"
                                style={{
                                    gridTemplateColumns: `repeat(${gridSize || 3}, 1fr)`,
                                    gridTemplateRows: `repeat(${gridSize || 3}, 1fr)`,
                                    position: 'relative',
                                    '--tile-size': `${tileSize}px`,
                                }}
                            >
                                {grid && grid.map((row, rIdx) =>
                                    row.map((cell, cIdx) => {
                                        const key = `${triggerPop}-${rIdx}-${cIdx}`;
                                        if (cell === 0) return <Tile key={key} shouldPop={triggerPop} tileType="blank" />;
                                        if (cell === 1) return <Tile key={key} shouldPop={triggerPop} tileType="wall" />;
                                        if (cell === 2) return <Tile key={key} shouldPop={triggerPop} tileType="goal" />;
                                        return null;
                                    })
                                )}

                                {tileSize > 0 && Object.entries(items).map(([posKey, type]) => {
                                    const [y, x] = posKey.split('-').map(Number);
                                    const style = {
                                        position: 'absolute',
                                        left: `${x * (tileSize + gapSize)}px`,
                                        top: `${y * (tileSize + gapSize)}px`,
                                        width: `${tileSize}px`,
                                        height: `${tileSize}px`,
                                        zIndex: 5,
                                    };
                                    return (
                                        <div key={posKey} style={style}>
                                            <Tile shouldPop={triggerPop} tileType="coin" />
                                        </div>
                                    );
                                })}

                                {collectEffects.map(effect => (
                                    <CollectEffect
                                        key={effect.id}
                                        x={effect.x}
                                        y={effect.y}
                                        size={effect.size}
                                        color={effect.color}
                                    />
                                ))}

                                {tileSize > 0 && playerPos && (
                                    <Player
                                        gridX={playerPos.x}
                                        gridY={playerPos.y}
                                        tileSize={tileSize}
                                        gapSize={gapSize}
                                        deathType={null}
                                        isGhost={false}
                                        skinId="default"
                                    />
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Completion banner — absolutely positioned over the grid */}
                    {banner && (
                        <div className={`tutorial-completion-banner ${bannerExiting ? 'banner-exit' : ''}`}>
                            <CheckCircle size={24} />
                            <span>{banner.text}</span>
                        </div>
                    )}
                </div>

                {/* Info display — summary step */}
                {!hasGrid && (
                    <div className="tutorial-info-display">
                        <div className="tutorial-timer-demo">
                            <div className="tutorial-timer-bar-container">
                                <div className="tutorial-timer-bar-fill" />
                            </div>
                            <p className="tutorial-timer-label">Each round is timed — reach the goal before the bar runs out!</p>
                        </div>
                        <div className="tutorial-info-items">
                            <div className="tutorial-info-row">
                                <Trophy size={18} className="tutorial-icon-green" />
                                <span>Score points by reaching goals</span>
                            </div>
                            <div className="tutorial-info-row">
                                <Timer size={18} className="tutorial-icon-blue" />
                                <span>The grid grows as your score rises</span>
                            </div>
                            <div className="tutorial-info-row">
                                <Coins size={18} className="tutorial-icon-yellow" />
                                <span>Spend coins on skins in the shop</span>
                            </div>
                        </div>
                    </div>
                )}

                {/* Controls hint — always reserves space on grid steps so layout doesn't shift */}
                {hasGrid && (
                    <div className="tutorial-controls-hint">
                        {isTouch ? (
                            <div className="hint-row">
                                <Move size={18} />
                                <span>Swipe to move</span>
                            </div>
                        ) : (
                            <div className="hint-row">
                                <div className="key-group">
                                    <span className="key-cap"><ArrowUp size={12} /></span>
                                    <div className="key-row-bottom">
                                        <span className="key-cap"><ArrowLeft size={12} /></span>
                                        <span className="key-cap"><ArrowDown size={12} /></span>
                                        <span className="key-cap"><ArrowRight size={12} /></span>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {isLastStep && (
                    <button className="tutorial-start-btn" onClick={handleFinish}>
                        <span>LET'S GO</span>
                        <ChevronRight size={20} />
                    </button>
                )}
            </div>
        </div>
    );
}

export default Tutorial;
